import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  BookOpen, Plus, Trash2, Play, Share2, ChevronDown, ChevronUp,
  Pencil, X, Check, Users, Lock, Flame
} from 'lucide-react';
import { toast } from 'sonner';

export function MealTemplates({ onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [applying, setApplying] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/meal-templates');
      setTemplates(data);
    } catch {
      toast.error('Erreur chargement templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleApply = async (id, name) => {
    setApplying(id);
    try {
      const { data } = await apiClient.post(`/meal-templates/${id}/apply`);
      toast.success(`"${name}" applique ! ${data.applied} repas ajoutes`);
      onApplied?.();
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setApplying(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/meal-templates/${id}`);
      toast.success('Template supprime');
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="meal-templates-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[#FF6B35]" />
          <h3 className="text-sm font-medium text-zinc-400">Journees types</h3>
          <span className="text-[10px] text-zinc-600">{templates.length} template{templates.length !== 1 ? 's' : ''}</span>
        </div>
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-1 text-xs text-[#FF6B35] font-semibold hover:text-[#FF8C55] transition-colors"
          data-testid="create-template-btn"
        >
          {showCreate ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
          {showCreate ? 'Annuler' : 'Creer'}
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <CreateTemplateForm
          onCreated={() => { setShowCreate(false); fetchTemplates(); }}
          onCancel={() => setShowCreate(false)}
        />
      )}

      {/* Templates List */}
      {templates.length === 0 && !showCreate ? (
        <div className="bg-[#0F0F0F] rounded-sm p-6 text-center">
          <BookOpen className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">Aucune journee type</p>
          <p className="text-xs text-zinc-600 mt-1">Cree un template ou sauvegarde une journee depuis l'historique</p>
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((t) => {
            const isExpanded = expanded === t.id;
            return (
              <div
                key={t.id}
                className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm overflow-hidden"
                data-testid={`template-${t.id}`}
              >
                {/* Template Header */}
                <div className="p-3 flex items-center gap-3">
                  <button
                    onClick={() => setExpanded(isExpanded ? null : t.id)}
                    className="flex-1 flex items-center gap-3 text-left min-w-0"
                    data-testid={`template-toggle-${t.id}`}
                  >
                    <div className="w-10 h-10 rounded-sm bg-[#FF6B35]/10 flex items-center justify-center flex-shrink-0">
                      <Flame className="w-5 h-5 text-[#FF6B35]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-bold text-white truncate">{t.name}</p>
                        {t.shared && <Users className="w-3 h-3 text-blue-400 flex-shrink-0" title="Partage" />}
                        {!t.is_mine && <Lock className="w-3 h-3 text-zinc-600 flex-shrink-0" />}
                      </div>
                      <p className="text-xs text-zinc-500">
                        {t.meals?.length} repas · <span className="text-[#FF6B35] font-mono font-bold">{t.totals?.calories}</span> kcal
                        {t.totals?.protein > 0 && <span className="ml-1">· P:{Math.round(t.totals.protein)}g</span>}
                      </p>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                  </button>

                  {/* Apply button */}
                  <Button
                    onClick={() => handleApply(t.id, t.name)}
                    disabled={applying === t.id}
                    size="sm"
                    className="bg-[#FF6B35] text-black hover:bg-[#E85A25] flex-shrink-0"
                    data-testid={`apply-template-${t.id}`}
                  >
                    {applying === t.id ? (
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <><Play className="w-3.5 h-3.5 mr-1" /> Appliquer</>
                    )}
                  </Button>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="border-t border-[#2A2A2E] bg-[#0F0F0F]">
                    {t.description && (
                      <p className="px-4 py-2 text-xs text-zinc-500 italic border-b border-[#2A2A2E]/50">{t.description}</p>
                    )}
                    <div className="divide-y divide-zinc-800/30">
                      {(t.meals || []).map((m, i) => (
                        <div key={i} className="px-4 py-2 flex items-center justify-between">
                          <p className="text-xs text-zinc-300 flex-1 truncate">{m.meal_name}</p>
                          <div className="flex gap-2 text-[10px] text-zinc-500 flex-shrink-0 ml-2">
                            <span className="font-mono text-white">{Math.round(m.calories)} kcal</span>
                            <span>P:{Math.round(m.protein)}g</span>
                            <span>G:{Math.round(m.carbs)}g</span>
                            <span>L:{Math.round(m.fat)}g</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Totals */}
                    <div className="px-4 py-2 border-t border-[#2A2A2E] flex items-center justify-between">
                      <span className="text-[10px] text-zinc-600 font-semibold uppercase">Total</span>
                      <div className="flex gap-2 text-[10px] font-bold">
                        <span className="text-[#FF6B35] font-mono">{t.totals?.calories} kcal</span>
                        <span className="text-zinc-400">P:{Math.round(t.totals?.protein || 0)}g</span>
                        <span className="text-zinc-400">G:{Math.round(t.totals?.carbs || 0)}g</span>
                        <span className="text-zinc-400">L:{Math.round(t.totals?.fat || 0)}g</span>
                      </div>
                    </div>
                    {/* Actions */}
                    {t.is_mine && (
                      <div className="px-4 py-2 border-t border-[#2A2A2E] flex justify-end gap-2">
                        <button
                          onClick={() => handleDelete(t.id)}
                          className="flex items-center gap-1 text-[10px] text-red-400 hover:text-red-300 transition-colors px-2 py-1"
                          data-testid={`delete-template-${t.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Supprimer
                        </button>
                      </div>
                    )}
                    {t.use_count > 0 && (
                      <div className="px-4 py-1.5 text-[9px] text-zinc-600">
                        Utilise {t.use_count} fois
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function CreateTemplateForm({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [shared, setShared] = useState(false);
  const [meals, setMeals] = useState([{ meal_name: '', calories: '', protein: '', carbs: '', fat: '', fiber: '' }]);
  const [saving, setSaving] = useState(false);

  const addMeal = () => {
    setMeals([...meals, { meal_name: '', calories: '', protein: '', carbs: '', fat: '', fiber: '' }]);
  };

  const removeMeal = (i) => {
    if (meals.length <= 1) return;
    setMeals(meals.filter((_, idx) => idx !== i));
  };

  const updateMeal = (i, field, value) => {
    const updated = [...meals];
    updated[i] = { ...updated[i], [field]: value };
    setMeals(updated);
  };

  const handleSubmit = async () => {
    if (!name.trim()) { toast.error('Nom requis'); return; }
    const validMeals = meals.filter(m => m.meal_name.trim() && parseFloat(m.calories) > 0);
    if (validMeals.length === 0) { toast.error('Au moins un repas avec des calories'); return; }
    setSaving(true);
    try {
      await apiClient.post('/meal-templates', {
        name: name.trim(),
        description: description.trim(),
        shared,
        meals: validMeals.map(m => ({
          meal_name: m.meal_name.trim(),
          calories: parseFloat(m.calories) || 0,
          protein: parseFloat(m.protein) || 0,
          carbs: parseFloat(m.carbs) || 0,
          fat: parseFloat(m.fat) || 0,
          fiber: parseFloat(m.fiber) || 0,
        })),
      });
      toast.success('Template cree !');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#1B1B1B] border border-[#FF6B35]/30 rounded-sm p-4 space-y-3" data-testid="create-template-form">
      <div className="space-y-2">
        <Label className="text-zinc-400 text-xs">Nom du template *</Label>
        <Input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Ex: Journee Seche, Jour de repos..."
          className="bg-[#0F0F0F] border-[#2A2A2E] text-white text-sm"
          data-testid="template-name-input"
        />
      </div>

      <div className="space-y-2">
        <Label className="text-zinc-400 text-xs">Description (optionnel)</Label>
        <Input
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Ex: Haute proteines, basses calories"
          className="bg-[#0F0F0F] border-[#2A2A2E] text-white text-sm"
          data-testid="template-desc-input"
        />
      </div>

      {/* Shared toggle */}
      <label className="flex items-center gap-2 cursor-pointer" data-testid="template-shared-toggle">
        <div className={`w-8 h-5 rounded-full transition-colors relative ${shared ? 'bg-[#FF6B35]' : 'bg-zinc-700'}`}
          onClick={() => setShared(!shared)}>
          <div className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${shared ? 'left-3.5' : 'left-0.5'}`} />
        </div>
        <span className="text-xs text-zinc-400 flex items-center gap-1">
          <Share2 className="w-3 h-3" /> Partager avec le groupe
        </span>
      </label>

      {/* Meals */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-zinc-400 text-xs">Repas ({meals.length})</Label>
          <button onClick={addMeal} className="text-[10px] text-[#FF6B35] font-semibold flex items-center gap-0.5 hover:text-[#FF8C55]" data-testid="add-meal-row-btn">
            <Plus className="w-3 h-3" /> Ajouter
          </button>
        </div>

        {meals.map((m, i) => (
          <div key={i} className="bg-[#0F0F0F] rounded-sm p-3 space-y-2 relative" data-testid={`meal-row-${i}`}>
            {meals.length > 1 && (
              <button onClick={() => removeMeal(i)}
                className="absolute top-2 right-2 text-zinc-600 hover:text-red-400 transition-colors"
                data-testid={`remove-meal-${i}`}>
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            <Input
              value={m.meal_name}
              onChange={e => updateMeal(i, 'meal_name', e.target.value)}
              placeholder="Nom du repas *"
              className="bg-transparent border-zinc-800 text-white text-xs"
              data-testid={`meal-name-${i}`}
            />
            <div className="grid grid-cols-5 gap-1.5">
              {[
                { key: 'calories', label: 'Kcal', color: 'text-[#FF6B35]' },
                { key: 'protein', label: 'Prot', color: 'text-red-400' },
                { key: 'carbs', label: 'Gluc', color: 'text-yellow-400' },
                { key: 'fat', label: 'Lip', color: 'text-blue-400' },
                { key: 'fiber', label: 'Fib', color: 'text-green-400' },
              ].map(({ key, label, color }) => (
                <div key={key}>
                  <p className={`text-[9px] ${color} mb-0.5 text-center`}>{label}</p>
                  <Input
                    type="number"
                    value={m[key]}
                    onChange={e => updateMeal(i, key, e.target.value)}
                    placeholder="0"
                    className="bg-transparent border-zinc-800 text-white text-xs text-center font-mono px-1"
                    data-testid={`meal-${key}-${i}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Submit */}
      <div className="flex gap-2 pt-1">
        <Button onClick={onCancel} variant="outline" size="sm" className="flex-1 border-zinc-700 text-zinc-400 hover:text-white" data-testid="cancel-template-btn">
          Annuler
        </Button>
        <Button onClick={handleSubmit} disabled={saving} size="sm" className="flex-1 bg-[#FF6B35] text-black hover:bg-[#E85A25]" data-testid="save-template-btn">
          {saving ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Enregistrer</>}
        </Button>
      </div>
    </div>
  );
}


export function SaveDayAsTemplate({ date, onSaved }) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Nom requis'); return; }
    setSaving(true);
    try {
      await apiClient.post('/meal-templates/from-day', {
        date,
        name: name.trim(),
        shared: false,
      });
      toast.success('Journee sauvegardee comme template !');
      setShowForm(false);
      setName('');
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="flex items-center gap-1 text-[10px] text-[#FF6B35] hover:text-[#FF8C55] transition-colors px-1"
        data-testid={`save-day-template-${date}`}
      >
        <BookOpen className="w-3 h-3" /> Sauvegarder
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 mt-1 px-1" data-testid={`save-day-form-${date}`}>
      <Input
        value={name}
        onChange={e => setName(e.target.value)}
        placeholder="Nom du template"
        className="bg-[#0F0F0F] border-zinc-800 text-white text-[10px] h-7 flex-1"
        autoFocus
        onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setShowForm(false); }}
        data-testid={`save-day-name-${date}`}
      />
      <button onClick={handleSave} disabled={saving} className="text-green-400 hover:text-green-300 p-0.5" data-testid={`save-day-confirm-${date}`}>
        <Check className="w-3.5 h-3.5" />
      </button>
      <button onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-zinc-300 p-0.5">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
